/**
 * useAdvancedOptions Hook
 *
 * Custom hook for managing advanced QR code options state.
 * Provides state management for optional query parameters that can be
 * added to generated QR URLs.
 *
 * Requirements: 7.1, 7.2, 7.3
 */

import { useState, useCallback } from 'react';
import { AdvancedOptions, DEFAULT_ADVANCED_OPTIONS } from '../types/advancedOptions';

/**
 * Return type for the useAdvancedOptions hook
 */
export interface UseAdvancedOptionsResult {
  /** Current advanced options state */
  readonly advancedOptions: AdvancedOptions;
  /** Update advanced options fields (partial updates supported) */
  readonly updateAdvancedOptions: (updates: Partial<AdvancedOptions>) => void;
  /** Reset advanced options to default values */
  readonly resetAdvancedOptions: () => void;
}

/**
 * Custom hook for managing advanced QR code options state.
 *
 * Features:
 * - Initializes with default advanced options values
 * - Maintains option values when template customization settings change (Requirement 7.1)
 * - Maintains toggle state when template customization settings change (Requirement 7.2)
 * - Preserves option values when toggle is disabled and re-enabled (Requirement 7.3)
 *
 * @returns Object containing advanced options state, update function, and reset function
 *
 * @example
 * ```tsx
 * const { advancedOptions, updateAdvancedOptions, resetAdvancedOptions } = useAdvancedOptions();
 *
 * // Enable advanced options
 * updateAdvancedOptions({ enabled: true });
 *
 * // Update a single option
 * updateAdvancedOptions({ experimentalFeatures: true });
 *
 * // Update multiple options
 * updateAdvancedOptions({ languageOverride: 'de', swingCapture: true });
 *
 * // Disable toggle (option values are preserved)
 * updateAdvancedOptions({ enabled: false });
 *
 * // Re-enable toggle (previously configured values are restored)
 * updateAdvancedOptions({ enabled: true });
 *
 * // Reset all options to defaults
 * resetAdvancedOptions();
 * ```
 */
export function useAdvancedOptions(): UseAdvancedOptionsResult {
  // Advanced options state - initialized with default values
  const [advancedOptions, setAdvancedOptions] = useState<AdvancedOptions>(DEFAULT_ADVANCED_OPTIONS);

  /**
   * Update advanced options with partial changes.
   * Merges updates with existing state, preserving unchanged fields.
   * Uses immutable update pattern for predictable state management.
   *
   * Note: When the enabled toggle is disabled, option values are preserved
   * in state so they can be restored when re-enabled (Requirement 7.3).
   */
  const updateAdvancedOptions = useCallback((updates: Partial<AdvancedOptions>): void => {
    setAdvancedOptions((prevOptions) => ({
      ...prevOptions,
      ...updates,
    }));
  }, []);

  /**
   * Reset advanced options to default values.
   * Clears all customizations including the enabled state.
   */
  const resetAdvancedOptions = useCallback((): void => {
    setAdvancedOptions(DEFAULT_ADVANCED_OPTIONS);
  }, []);

  return {
    advancedOptions,
    updateAdvancedOptions,
    resetAdvancedOptions,
  };
}
